import { pick } from "@/lib/languages";
import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import LocalePicker from "@/components/layout/LocalePicker";
import { useSettings } from "@/context/SettingsContext";
import { footerL as L } from "@/i18n/common";

/**
 * Every entry here is a real link, and each destination appears once: the
 * footer used to list "Contact" twice and send both "Manage" and "Cancel" to
 * /manage. The products follow the header's names and order, Airport taxis
 * included. On a phone each link is a full 44px row. Social icons are not
 * shown until the accounts exist.
 */
const COL = ({ title, items }) => (
  <nav aria-label={title} className="min-w-0">
    <h2 className="mb-1 text-label-lg font-bold text-white md:mb-2">{title}</h2>
    <ul className="md:space-y-1">
      {items.map(([label, to]) => (
        <li key={to}>
          <Link to={to} className="flex min-h-11 items-center text-body-sm text-touki-on-ink-muted underline-offset-4 transition-colors duration-fast hover:text-white hover:underline md:inline-flex md:min-h-8">
            {label}
          </Link>
        </li>
      ))}
    </ul>
  </nav>
);

export default function Footer() {
  const { lang, t } = useSettings();
  const l = pick(L, lang);
  return (
    <footer className="tk-on-ink mt-16 bg-touki-dark-surface text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 pb-6 pt-10 sm:px-6 lg:px-8 lg:pt-12">
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-4 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr] lg:gap-x-10">
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <Logo size={30} wordmarkClass="text-xl text-white" />
            <p className="mt-3 max-w-xs text-body-sm text-touki-on-ink-muted text-pretty">{l.tagline}</p>
          </div>
          <COL title={l.explore} items={[
            [t("nav.stays"), "/hotels"], [t("nav.flights"), "/flights"], [t("nav.flightHotel"), "/flight-hotel"],
            [t("nav.cars"), "/cars"], [t("nav.attractions"), "/attractions"], [t("nav.taxis"), "/airport-taxis"],
            [t("nav.tripGroupe"), "/trip-groupe"],
          ]} />
          <COL title={l.support} items={[[l.manage, "/manage"], [l.contact, "/support"], [l.faq, "/faq"], [l.refund, "/refund-policy"]]} />
          <COL title={l.company} items={[[l.about, "/about"], [l.partners, "/partners"]]} />
          <COL title={l.legal} items={[[l.terms, "/terms"], [l.privacy, "/privacy"], [l.cookies, "/cookies"]]} />
        </div>
        <div className="mt-10 flex flex-col-reverse gap-4 border-t border-white/15 pt-5 text-body-sm text-touki-on-ink-muted sm:flex-row sm:items-center sm:justify-between">
          <p>{l.rights}</p>
          {/* The visitor's own setting, and the control to change it: this line
              used to say "English (US) · USD $" to a visitor who had chosen
              French and XOF, and could not be clicked. */}
          <LocalePicker variant="footer" className="self-start sm:self-auto" />
        </div>
      </div>
    </footer>
  );
}
